import { Radio, Select, Space } from 'antd';
import dayjs from 'dayjs';
import React from 'react';

import sharedStyles from '../../sharedStyles.module.scss';
import type {
  CleanKindAndReservationRequest,
  CleanKindResult,
  MonthReservationLimit,
} from '../../sharedTypes';
import { days } from '../helper';
import DailyReservationCard from './DailyReservationCard';
import { getMonthOptions, getYearOptions } from './helper';
import styles from './index.module.scss';

type DailyReservationLimitProps = {
  showType: CleanKindAndReservationRequest['dataType'];
  selectedOptions: string[];
  stateSetters: {
    setShowType: React.Dispatch<
      React.SetStateAction<CleanKindAndReservationRequest['dataType']>
    >;
    setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>;
    setSelectedYear: React.Dispatch<React.SetStateAction<number>>;
    setSelectedMonth: React.Dispatch<React.SetStateAction<number>>;
  };
  cleanKindAndReservationLimitList:
    | {
        kinds: CleanKindResult[];
        dailyReservationLimits: MonthReservationLimit[][];
      }
    | undefined;
  handleFiltersChange: (
    key?: string,
    value?:
      | string
      | number
      | CleanKindAndReservationRequest['dataType']
      | string[]
  ) => void;
};

const { Option } = Select;

const DailyReservationLimit = ({
  showType,
  stateSetters,
  selectedOptions,
  cleanKindAndReservationLimitList,
  handleFiltersChange,
}: DailyReservationLimitProps) => {
  const { setShowType, setSelectedOptions, setSelectedYear, setSelectedMonth } =
    stateSetters;

  const yearOptions = getYearOptions();
  const monthOptions = getMonthOptions();
  const kindOptions = cleanKindAndReservationLimitList?.kinds.map((kind) => ({
    label: kind.name,
    value: kind.cleanKindId,
  }));

  return (
    <div className={styles.grayBackground}>
      <Space direction="vertical" className={sharedStyles.widthFull}>
        <span className={styles.tableTitle}>每日清潔服務預約次數設定</span>
        <Space size="large">
          <div>
            <span>年份：</span>
            <Select
              placeholder="請選擇年份"
              className={styles.selector}
              options={yearOptions}
              defaultValue={dayjs().year()}
              onChange={(value) => {
                setSelectedYear(value);
                handleFiltersChange('year', value);
              }}
            />
          </div>
          <div>
            <span>月份：</span>
            <Select
              placeholder="請選擇月份"
              className={styles.selector}
              options={monthOptions}
              defaultValue={monthOptions[dayjs().month()].value}
              onChange={(value) => {
                setSelectedMonth(value);
                handleFiltersChange('month', value);
              }}
            />
          </div>
          <div>
            <span>顯示：</span>
            <Radio.Group
              onChange={(event) => {
                setShowType(event.target.value);
                handleFiltersChange('dataType', event.target.value);
              }}
              value={showType}
            >
              <Radio.Button value="Limit">上限次數</Radio.Button>
              <Radio.Button value="Remaining">剩餘次數</Radio.Button>
            </Radio.Group>
          </div>
          <div>
            <span>類別：</span>
            <Select
              status={selectedOptions.length > 3 ? 'error' : ''}
              className={styles.kindSelector}
              placeholder="請選擇類別"
              allowClear
              mode="multiple"
              showArrow
              showSearch={false}
              maxTagCount={3}
              onChange={(value: string[]) => {
                setSelectedOptions(value);
                handleFiltersChange('cleanKindIdList', value);
              }}
            >
              {kindOptions?.map(({ label, value }) => {
                const isSelected = selectedOptions.includes(value);
                return (
                  <Option
                    key={value}
                    value={value}
                    disabled={
                      isSelected === false && selectedOptions.length === 3
                    }
                  >
                    {label}
                  </Option>
                );
              })}
            </Select>
            <span
              className={styles.showSelectedNumber}
            >{`(${selectedOptions.length}/3)`}</span>
          </div>
        </Space>
        <div className={styles.days}>
          {days.map((day) => (
            <div key={day}>{day}</div>
          ))}
        </div>
        {cleanKindAndReservationLimitList?.dailyReservationLimits.map(
          (data, index) => {
            let weekData = data;

            const firstDateIndex = dayjs(data[0].date).day();

            if (firstDateIndex !== 0) {
              const notThisMonth = Array.from({ length: firstDateIndex }).map(
                (_, index) => {
                  const placeholderData: MonthReservationLimit = {
                    date: dayjs(data[0].date)
                      .subtract(firstDateIndex - index, 'day')
                      .valueOf(),
                    result: [],
                  };
                  return placeholderData;
                }
              );
              weekData = notThisMonth.concat(weekData);
            }

            return (
              <div key={index} className={styles.gridContainer}>
                {weekData.map(({ date, result }) => {
                  const isThisMonth =
                    dayjs(date).get('month') ===
                    dayjs(data[0].date).get('month');
                  if (isThisMonth === false) {
                    return <div key={date} />;
                  }
                  return (
                    <DailyReservationCard
                      key={date}
                      date={date}
                      result={result}
                      showType={showType}
                      getLatestLimitList={handleFiltersChange}
                    />
                  );
                })}
              </div>
            );
          }
        )}
      </Space>
    </div>
  );
};

export default DailyReservationLimit;
